<script>
import { mapGetters } from "vuex";
import DatePicker from 'vue-datepicker-next';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';

const DEFAULT_LOCAL = {
  query: "",
  ownerIds: [],
  dateRange: [],
  amountMin: null,
  amountMax: null,
};

export default {
  name: "DealFilters",
  components: { DatePicker },
  data() {
    return {
      local: { ...DEFAULT_LOCAL },
      showOwnerPopover: false,
      showAmountPopover: false,
      showDatePicker: false,
      _searchTimer: null,
    };
  },
  computed: {
    ...mapGetters("crmBoard", [
      "hasActiveFilters",
      "activeFilterCount",
      "pipelineOwners",
    ]),
    ownerList() {
      // Usar pipelineOwners del store (owners reales de deals cargados)
      return this.pipelineOwners;
    },
    dateFilterActive() {
      return this.local.dateRange.length === 2;
    },
    amountFilterActive() {
      return this.local.amountMin != null || this.local.amountMax != null;
    },
  },
  mounted() {
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeUnmount() {
    clearTimeout(this._searchTimer);
    document.removeEventListener("click", this.handleOutsideClick);
  },
  methods: {
    handleOutsideClick(e) {
      if (!this.$el.contains(e.target)) {
        this.showOwnerPopover = false;
        this.showAmountPopover = false;
      }
    },
    onQueryInput(e) {
      this.local.query = e.target.value;
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.$store.dispatch("crmBoard/applyFilters", { query: this.local.query });
      }, 300);
    },
    toggleOwner(id) {
      const idx = this.local.ownerIds.indexOf(id);
      if (idx === -1) {
        this.local.ownerIds = [...this.local.ownerIds, id];
      } else {
        this.local.ownerIds = this.local.ownerIds.filter((oid) => oid !== id);
      }
      this.$store.dispatch("crmBoard/applyFilters", {
        ownerIds: [...this.local.ownerIds],
      });
    },
    onDateRangeChange(dates) {
      this.local.dateRange = dates;
      this.$store.dispatch("crmBoard/applyFilters", {
        createdAtFrom: getUnixStartOfDay(dates[0]),
        createdAtTo: getUnixEndOfDay(dates[1]),
      });
    },
    clearDateFilter() {
      this.local.dateRange = [];
      this.$store.dispatch("crmBoard/applyFilters", {
        createdAtFrom: null,
        createdAtTo: null,
      });
    },
    applyAmountFilter() {
      this.$store.dispatch("crmBoard/applyFilters", {
        amountMin:
          this.local.amountMin !== "" && this.local.amountMin != null
            ? Number(this.local.amountMin)
            : null,
        amountMax:
          this.local.amountMax !== "" && this.local.amountMax != null
            ? Number(this.local.amountMax)
            : null,
      });
      this.showAmountPopover = false;
    },
    clearAmountFilter() {
      this.local.amountMin = null;
      this.local.amountMax = null;
      this.$store.dispatch("crmBoard/applyFilters", {
        amountMin: null,
        amountMax: null,
      });
      this.showAmountPopover = false;
    },
    clearAll() {
      clearTimeout(this._searchTimer);
      this.local = { ...DEFAULT_LOCAL };
      this.$store.dispatch("crmBoard/applyFilters", {
        query: '',
        ownerIds: [],
        createdAtFrom: null,
        createdAtTo: null,
        amountMin: null,
        amountMax: null,
      });
    },
  },
};
</script>

<template>
  <div
    class="flex items-center gap-3 py-2 px-6 flex-wrap bg-white dark:bg-n-solid-3 border-b border-n-weak"
  >
    <!-- Búsqueda de texto -->
    <div class="flex-shrink-0 w-[400px] flex items-center gap-3 px-3 py-2 border border-n-weak rounded-xl bg-white dark:bg-n-solid-2 focus-within:ring-2 focus-within:ring-woot-500/30 focus-within:border-woot-400 transition-colors">
      <svg
        class="w-5 h-5 flex-shrink-0 text-n-slate-10 pointer-events-none"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z"
        />
      </svg>
      <input
        :value="local.query"
        type="text"
        :placeholder="$t('CRM.DEAL_FILTERS.SEARCH_PLACEHOLDER')"
        class="flex-1 bg-transparent outline-none border-0 p-0 text-sm text-n-slate-12 placeholder-n-slate-10 min-w-0"
        style="margin: 0;"
        @input="onQueryInput"
      />
    </div>

    <!-- Filtro de responsable -->
    <div class="relative">
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors cursor-pointer"
        :class="
          local.ownerIds.length
            ? 'border-woot-500 text-woot-700 bg-woot-50 dark:bg-woot-900/20 dark:text-woot-300'
            : 'border-n-weak text-n-slate-11 hover:border-n-slate-9 bg-white dark:bg-n-solid-2'
        "
        @click.stop="
          showOwnerPopover = !showOwnerPopover;
          showAmountPopover = false;
        "
      >
        <svg
          class="w-3.5 h-3.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
        {{ $t("CRM.DEAL_FILTERS.OWNER_LABEL") }}
        <span
          v-if="local.ownerIds.length"
          class="bg-woot-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center leading-none"
        >
          {{ local.ownerIds.length }}
        </span>
      </button>

      <div
        v-if="showOwnerPopover"
        class="absolute top-9 left-0 z-50 bg-white dark:bg-n-solid-3 border border-n-weak rounded-lg shadow-lg p-2 min-w-[190px] max-h-52 overflow-y-auto"
      >
        <p
          v-if="ownerList.length === 0"
          class="text-xs text-n-slate-10 px-2 py-1.5"
        >
          {{ $t("CRM.DEAL_FILTERS.NO_OWNERS") }}
        </p>
        <label
          v-for="agent in ownerList"
          :key="agent.id"
          class="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-n-slate-9/5 cursor-pointer text-sm text-n-slate-12"
        >
          <input
            type="checkbox"
            class="rounded"
            :checked="local.ownerIds.includes(agent.id)"
            @change="toggleOwner(agent.id)"
          />
          {{ agent.name }}
        </label>
      </div>
    </div>

    <!-- Filtro de fecha de creación -->
    <div class="relative flex items-center gap-1">
      <DatePicker
        v-model:value="local.dateRange"
        range
        confirm
        :clearable="false"
        :editable="false"
        :confirm-text="$t('CRM.DEAL_FILTERS.APPLY')"
        :placeholder="$t('CRM.DEAL_FILTERS.DATE_LABEL')"
        @change="onDateRangeChange"
      >
        <template #input>
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors cursor-pointer"
            :class="
              dateFilterActive
                ? 'border-woot-500 text-woot-700 bg-woot-50 dark:bg-woot-900/20 dark:text-woot-300'
                : 'border-n-weak text-n-slate-11 hover:border-n-slate-9 bg-white dark:bg-n-solid-2'
            "
          >
            <svg
              class="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {{ $t('CRM.DEAL_FILTERS.DATE_LABEL') }}
            <span
              v-if="dateFilterActive"
              class="w-2 h-2 rounded-full bg-woot-500 inline-block"
            />
          </button>
        </template>
      </DatePicker>
      <button
        v-if="dateFilterActive"
        class="p-1 text-n-slate-10 hover:text-red-500 transition-colors cursor-pointer"
        :title="$t('CRM.DEAL_FILTERS.CLEAR')"
        @click.stop="clearDateFilter"
      >
        <svg
          class="w-3.5 h-3.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>

    <!-- Filtro de monto -->
    <div class="relative">
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors cursor-pointer"
        :class="
          amountFilterActive
            ? 'border-woot-500 text-woot-700 bg-woot-50 dark:bg-woot-900/20 dark:text-woot-300'
            : 'border-n-weak text-n-slate-11 hover:border-n-slate-9 bg-white dark:bg-n-solid-2'
        "
        @click.stop="
          showAmountPopover = !showAmountPopover;
          showOwnerPopover = false;
        "
      >
        <svg
          class="w-3.5 h-3.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        {{ $t("CRM.DEAL_FILTERS.AMOUNT_LABEL") }}
        <span
          v-if="amountFilterActive"
          class="w-2 h-2 rounded-full bg-woot-500 inline-block"
        />
      </button>

      <div
        v-if="showAmountPopover"
        class="absolute top-9 left-0 z-50 bg-white dark:bg-n-solid-3 border border-n-weak rounded-lg shadow-lg p-3 min-w-[200px]"
      >
        <p class="text-xs font-medium text-n-slate-11 mb-2">
          {{ $t("CRM.DEAL_FILTERS.AMOUNT_TITLE") }}
        </p>
        <div class="space-y-2">
          <div>
            <label class="text-xs text-n-slate-10 block mb-1">{{
              $t("CRM.DEAL_FILTERS.AMOUNT_MIN")
            }}</label>
            <input
              v-model="local.amountMin"
              type="number"
              min="0"
              class="w-full text-sm border border-n-weak rounded px-2 py-1 bg-white dark:bg-n-solid-2 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-400"
            />
          </div>
          <div>
            <label class="text-xs text-n-slate-10 block mb-1">{{
              $t("CRM.DEAL_FILTERS.AMOUNT_MAX")
            }}</label>
            <input
              v-model="local.amountMax"
              type="number"
              min="0"
              :placeholder="$t('CRM.DEAL_FILTERS.AMOUNT_NO_LIMIT')"
              class="w-full text-sm border border-n-weak rounded px-2 py-1 bg-white dark:bg-n-solid-2 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-400"
            />
          </div>
          <div class="flex gap-2 pt-1">
            <button
              class="flex-1 py-1.5 bg-woot-500 hover:bg-woot-600 text-white text-sm rounded transition-colors cursor-pointer"
              @click="applyAmountFilter"
            >
              {{ $t("CRM.DEAL_FILTERS.APPLY") }}
            </button>
            <button
              v-if="amountFilterActive"
              class="px-3 py-1.5 border border-n-weak text-n-slate-11 text-sm rounded hover:bg-n-slate-9/5 transition-colors cursor-pointer"
              @click="clearAmountFilter"
            >
              {{ $t("CRM.DEAL_FILTERS.CLEAR") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Badge clear all -->
    <button
      v-if="hasActiveFilters"
      class="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors cursor-pointer"
      @click="clearAll"
    >
      <svg
        class="w-3.5 h-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
      {{ activeFilterCount }}
      {{
        activeFilterCount === 1
          ? $t("CRM.DEAL_FILTERS.ACTIVE_FILTER_SINGULAR")
          : $t("CRM.DEAL_FILTERS.ACTIVE_FILTER_PLURAL")
      }}
    </button>
  </div>
</template>

<style scoped>
/* Forzar ancho auto en el wrapper del range date picker (por defecto: 320px) */
:deep(.mx-datepicker.mx-datepicker-range) {
  width: auto;
}
/* Ocultar el ícono de almanaque que agrega vue-datepicker-next automáticamente */
:deep(.mx-icon-calendar),
:deep(.mx-icon-clear) {
  display: none;
}
</style>
